import { useCallback, useEffect, useMemo, useState } from 'react';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/apiHeaders';
import { PHASE1_CAPABILITY_IDS } from '../lib/vendor-packs/constants.ts';
import type { FirmCapabilitiesResponse } from '../types/vendorPacks';

/** Legacy default = Phase 1 union (core-itsm + atlassian-jsm effective caps). Used while loading or on fetch failure. */
const LEGACY_CAPABILITY_SET = new Set<string>(PHASE1_CAPABILITY_IDS);

export const CAPABILITY_ATLASSIAN_UI = 'pathkit.atlassian.ui' as const;
export const CAPABILITY_ATLASSIAN_INTEGRATION = 'pathkit.atlassian.integration' as const;

type FetchStatus = 'idle' | 'loading' | 'ok' | 'error';

/**
 * Loads firm capabilities from GET /firms/:firmId/capabilities.
 * While loading, on error, or without firmId/token: effective capabilities = legacy Phase 1 set (permissive).
 */
export function useFirmCapabilities(
  firmId: string | null | undefined,
  accessToken: string | null | undefined,
): {
  hasCapability: (capabilityId: string) => boolean;
  loading: boolean;
  error: boolean;
  capabilities: string[];
  /**
   * True only when a `FirmCapabilitiesResponse` was successfully fetched for the current
   * (firmId, accessToken) pair. False for idle / loading / error states.
   *
   * Strict gating pattern (Phase 3B prep): consumers that render Atlassian-only entry
   * points should require `ready && hasCapability(...)` so loading/error states do not
   * fall through to the permissive `PHASE1_CAPABILITY_IDS` fallback. The fallback is
   * intentionally preserved for consumers that have not opted into strict gating.
   */
  ready: boolean;
} {
  const [status, setStatus] = useState<FetchStatus>('idle');
  const [data, setData] = useState<FirmCapabilitiesResponse | null>(null);

  useEffect(() => {
    if (!firmId || !accessToken) {
      setStatus('idle');
      setData(null);
      return;
    }

    let cancelled = false;
    setStatus('loading');
    setData(null);

    const url =
      `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${encodeURIComponent(firmId)}/capabilities`;

    (async () => {
      try {
        const res = await fetch(url, { headers: getAuthHeaders(accessToken) });
        if (!res.ok) {
          throw new Error(`capabilities ${res.status}`);
        }
        const body = (await res.json()) as FirmCapabilitiesResponse;
        if (!cancelled) {
          setData(body);
          setStatus('ok');
        }
      } catch (e) {
        console.warn('[useFirmCapabilities] fetch failed, using legacy capability fallback', e);
        if (!cancelled) {
          setData(null);
          setStatus('error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [firmId, accessToken]);

  const effectiveSet = useMemo(() => {
    if (status === 'ok' && data?.capabilities && Array.isArray(data.capabilities)) {
      return new Set(data.capabilities);
    }
    return LEGACY_CAPABILITY_SET;
  }, [status, data]);

  const hasCapability = useCallback(
    (capabilityId: string) => effectiveSet.has(capabilityId),
    [effectiveSet],
  );

  const loading = Boolean(firmId && accessToken && status === 'loading');
  const error = status === 'error';
  const ready = status === 'ok' && data !== null;
  const capabilities = useMemo(() => [...effectiveSet].sort(), [effectiveSet]);

  return { hasCapability, loading, error, capabilities, ready };
}
